import { ChangeDetectionStrategy, Component, ElementRef, computed, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { WorkflowNodeConfigBase } from './node-config-base';
import { WEBHOOK_METHOD_OPTIONS } from './config-options';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';

interface WebhookMappingRow {
  jsonPath: string;
  targetKey: string;
}

/** ACTION → Webhook: SEND_WEBHOOK. Portado 1:1 del panel viejo. */
@Component({
  selector: 'app-wf-webhook-action-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective, WorkflowVariablePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="space-y-4">
      <div>
        <div class="mb-1.5 flex items-center justify-between">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">URL</label>
          <app-wf-variable-picker [target]="urlField()?.nativeElement ?? null" [disabled]="readOnly()"
                                  (valueChange)="patchConfig({ url: $event })" />
        </div>
        <input #urlInput type="text" [class]="fieldClass" [value]="str('url')" [disabled]="readOnly()"
               placeholder="https://…" (input)="patchConfig({ url: inputValue($event) })" />
      </div>
      <div>
        <label [class]="labelClass">Método</label>
        <select [class]="fieldClass" [value]="str('method', 'POST')" [disabled]="readOnly()"
                (change)="patchConfig({ method: inputValue($event) })">
          @for (m of methodOptions; track m.value) { <option [value]="m.value">{{ m.label }}</option> }
        </select>
      </div>
      <div>
        <label [class]="labelClass">Cabeceras (JSON)</label>
        <textarea rows="3" [class]="jsonAreaClass" [value]="jsonOf('headers')" [disabled]="readOnly()"
                  (change)="setJson('headers', inputValue($event))"></textarea>
      </div>
      <div>
        <label [class]="labelClass">Parámetros de consulta (JSON)</label>
        <textarea rows="2" [class]="jsonAreaClass" [value]="jsonOf('queryParams')" [disabled]="readOnly()"
                  (change)="setJson('queryParams', inputValue($event))"></textarea>
      </div>
      <div>
        <div class="mb-1.5 flex items-center justify-between">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Cuerpo (JSON)</label>
          <app-wf-variable-picker [target]="bodyField()?.nativeElement ?? null" [disabled]="readOnly()"
                                  (valueChange)="setJson('body', $event)" />
        </div>
        <textarea #bodyInput rows="4" [class]="jsonAreaClass" [value]="jsonOf('body')" [disabled]="readOnly()"
                  (change)="setJson('body', inputValue($event))"></textarea>
      </div>
      <div>
        <label [class]="labelClass">Tiempo máximo de espera (segundos)</label>
        <input type="number" min="1" [class]="fieldClass" [value]="num('timeoutSeconds', 15)" [disabled]="readOnly()"
               (change)="patchConfig({ timeoutSeconds: numberValue($event) })" />
      </div>

      <div class="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
        <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <input type="checkbox" class="rounded border-gray-300" [checked]="retryEnabled()" [disabled]="readOnly()"
                 (change)="setRetryEnabled(checkboxValue($event))" />
          Reintentar si falla
        </label>
        @if (retryEnabled()) {
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Máx. intentos</label>
              <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('retry', 'maxAttempts', 3)" [disabled]="readOnly()"
                     (change)="patchNested('retry', { enabled: true, maxAttempts: numberValue($event) })" />
            </div>
            <div>
              <label [class]="labelClass">Espera entre reintentos (segundos)</label>
              <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('retry', 'backoffSeconds', 60)" [disabled]="readOnly()"
                     (change)="patchNested('retry', { enabled: true, backoffSeconds: numberValue($event) })" />
            </div>
          </div>
        }
      </div>

      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Mapeo de respuesta</label>
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addMapping()">+ Añadir</button>
        </div>
        @for (row of mappings(); track $index) {
          <div class="flex items-center gap-2">
            <input type="text" [class]="fieldClass" [value]="row.jsonPath" [disabled]="readOnly()" placeholder="jsonPath"
                   (input)="updateMapping($index, { jsonPath: inputValue($event) })" />
            <input type="text" [class]="fieldClass" [value]="row.targetKey" [disabled]="readOnly()" placeholder="targetKey"
                   (input)="updateMapping($index, { targetKey: inputValue($event) })" />
            <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                    [disabled]="readOnly()" (click)="removeMapping($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
          </div>
        }
      </div>

      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </div>
  `
})
export class WorkflowWebhookActionConfigComponent extends WorkflowNodeConfigBase {
  protected readonly methodOptions = WEBHOOK_METHOD_OPTIONS;
  protected readonly jsonAreaClass =
    'w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-[11px] text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-white/90';

  protected readonly urlField = viewChild<ElementRef<HTMLInputElement>>('urlInput');
  protected readonly bodyField = viewChild<ElementRef<HTMLTextAreaElement>>('bodyInput');

  protected readonly retryEnabled = computed(() => this.nested('retry')['enabled'] === true);

  protected readonly mappings = computed<WebhookMappingRow[]>(() =>
    this.arr('responseMapping').map((item) => {
      const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return { jsonPath: String(row['jsonPath'] ?? ''), targetKey: String(row['targetKey'] ?? '') };
    })
  );

  protected setRetryEnabled(enabled: boolean): void {
    if (enabled) {
      this.patchNested('retry', { enabled: true, maxAttempts: this.nestedNum('retry', 'maxAttempts', 3), backoffSeconds: this.nestedNum('retry', 'backoffSeconds', 60) });
    } else {
      this.patchConfig({ retry: { enabled: false } });
    }
  }

  protected addMapping(): void {
    this.patchConfig({ responseMapping: [...this.mappings(), { jsonPath: '', targetKey: '' }] });
  }

  protected updateMapping(index: number, patch: Partial<WebhookMappingRow>): void {
    this.patchConfig({ responseMapping: this.mappings().map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  }

  protected removeMapping(index: number): void {
    this.patchConfig({ responseMapping: this.mappings().filter((_, i) => i !== index) });
  }
}
